/**
 * hireos — the single Astryx theme for both apps (web + admin).
 *
 * One theme by decision (2026-07): the old warm/cool split is retired; admin
 * inherits the customer-facing brand look (Hanken Grotesk, warm neutrals,
 * deep-green accent). Runtime-injected via <Theme> in each app's main.tsx —
 * both apps are client-only SPAs, so no built-CSS step is needed yet.
 *
 * DESIGN.md rules encoded here rather than per-component:
 * - shadows zeroed: separation comes from borders, never elevation
 * - hover inset zeroed: no visual change on hover anywhere
 * - press scale off: no active/pressed state styling
 * Astryx's own motion (durations/easing) is deliberately left enabled.
 */
import { defineTheme } from "@astryxdesign/core/theme";

const FONT_FALLBACKS = "'Geist Sans', system-ui, -apple-system, sans-serif";

export const hireosTheme = defineTheme({
  name: "hireos",
  color: { accent: "#3B6B52", neutralStyle: "warm" },
  typography: {
    body: { family: "Hanken Grotesk", fallbacks: FONT_FALLBACKS },
    heading: { family: "Hanken Grotesk", fallbacks: FONT_FALLBACKS },
  },
  radius: { base: 6, multiplier: 0.75 },
  tokens: {
    "--color-accent": ["#3B6B52", "#6F987E"],
    "--shadow-low": "none",
    "--shadow-med": "none",
    "--shadow-high": "none",
    "--shadow-inset-hover": "none",
    // Astryx derives the popover surface from the neutral scale, which the
    // warm+green palette tints visibly green. Every popup surface (dropdown
    // menus, tab overflow menus) is plain white, like the app's own Select.
    "--color-background-popover": ["#ffffff", "#28292C"],
    // The overlay Astryx paints behind hovered/focused rows. Zeroed for the
    // same reason as --shadow-inset-hover: nothing changes on hover, and a
    // menu that opens with its first row already tinted reads as pre-hovered.
    "--color-overlay-hover": ["transparent", "transparent"],
    // The surface is white, not the warm off-white Astryx derives (see the
    // switch thumb below). One token, so a scope that turns the surface
    // black (web's `.dark`, DESIGN.md) turns every component on it too.
    "--color-background-surface": "#ffffff",
  },
  components: {
    // The switch thumb defaults to --color-background-surface (a warm
    // off-white that reads faintly green against the track). The thumb must
    // be pure white in both states.
    "switch-thumb": {
      base: { backgroundColor: "#ffffff" },
    },
    // The tooltip bubble is brand green with white text, one notch down in
    // size and tight enough that a short hint reads as one block — against
    // Astryx's own invert to text-primary-on-surface, which read as near-black
    // with blue-fringed near-white text, and its inheritance of the trigger's
    // casing/tracking (a table header forced the whole bubble uppercase).
    // Admin renders the bubble itself (#346 — Astryx's anchor positioning is
    // unsupported on iOS Safari), but keeps the stable `astryx-tooltip` class
    // so its palette is still THEMED here rather than forked per app. Geometry
    // — padding, radius, max-width — is the component's (see Tooltip.tsx).
    tooltip: {
      base: {
        backgroundColor: "var(--color-accent)",
        color: "#ffffff",
        fontSize: "0.875rem",
        lineHeight: "1.35",
        fontWeight: "400",
        textTransform: "none",
        letterSpacing: "normal",
      },
    },
    // The bar's own padding is zeroed: AppShell wraps it in the same
    // container the pages use, so nav items line up with page content
    // instead of sitting against the viewport edge.
    "top-nav": {
      base: { paddingInline: "0" },
    },
    // Astryx marks the current destination with a --color-neutral pill, which
    // on a flat page reads as a stuck hover. Text colour alone carries it:
    // bolding the selected item shifts the width of every label around it,
    // so the bar's spacing changes as the operator moves between pages.
    "top-nav-item": {
      base: { paddingInline: "0", marginInlineEnd: "1.25rem" },
      selected: {
        backgroundColor: "transparent",
        fontWeight: "var(--font-weight-normal)",
      },
    },
    // Menus sit on a hairline, never elevation (DESIGN.md Elevation).
    "dropdown-menu": {
      base: { borderWidth: "1px", borderStyle: "solid", borderColor: "#e5e7eb" },
    },
    "tab-menu-dropdown": {
      base: { borderWidth: "1px", borderStyle: "solid", borderColor: "#e5e7eb" },
    },
    button: {
      base: { "--button-press-scale": "none" },
      // Astryx fills secondary with --color-neutral, which reads as a stuck
      // hover state. DESIGN.md's secondary is a neutral surface with a border.
      "variant:secondary": {
        backgroundColor: "var(--color-background-surface)",
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: "#e5e7eb",
      },
      // Our legacy Button API has an `outline` variant Astryx lacks; the
      // wrapper maps to this custom variant.
      "variant:outline": {
        backgroundColor: "transparent",
        color: "var(--color-accent)",
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: "var(--color-accent)",
      },
    },
  },
});
